/**
 * Provisioning step of the onboarding wizard
 * Shows real-time progress of resource provisioning
 */
import {
  DevHubBox,
  DevHubAlert,
  DevHubProgressBar,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { IdeaPreviewCard } from '../../IdeaPreviewCard';
import {
  OnboardingFormData,
  ProvisioningComponent,
  CreatedAppData,
} from '../types';
import { GithubRepo } from '@devxdiscover/devhub-common';
import { EVENT_TYPES } from '../constants';

interface ProvisioningStepProps {
  formData: OnboardingFormData;
  mutationError: string | null;
  overallProgress: number;
  provisioningComponents: ProvisioningComponent[];
  createdAppData: CreatedAppData | null;
  adGroups: Array<{ groupName: string; groupId: string }>;
  githubRepos: GithubRepo[];
  isRetrying: boolean;
  isFailed: boolean;
  hasRetried: boolean;
  onRetry: () => void;
}

/**
 * Provisioning step component with progress tracking
 * @param props - Component props
 */
export const ProvisioningStep = ({
  formData,
  mutationError,
  overallProgress,
  provisioningComponents,
  createdAppData,
  adGroups,
  githubRepos,
  isRetrying,
  isFailed,
  hasRetried,
  onRetry,
}: ProvisioningStepProps) => {
  return (
    <DevHubBox>
      {/* Error Alert - show for mutation errors */}
      {mutationError && (
        <DevHubBox mb={2}>
          <DevHubAlert severity="error">
            <strong>Error:</strong> {mutationError}
          </DevHubAlert>
        </DevHubBox>
      )}

      {/* Overall Progress Bar - show only during initial provisioning, hide during retry */}
      {!mutationError &&
        !isRetrying &&
        !provisioningComponents.some(c => c.status === 'failed') && (
          <DevHubBox mb={3}>
            <DevHubProgressBar value={overallProgress} size="large" />
          </DevHubBox>
        )}

      {/* Preview Card with Embedded Progress */}
      {!mutationError && (
        <IdeaPreviewCard
          name={formData.name}
          description={formData.description}
          applicationId={createdAppData?.applicationId ?? null}
          appCreationStatus={
            provisioningComponents.find(
              c => c.id === EVENT_TYPES.APPLICATION,
            ) || provisioningComponents[0]
          }
          adGroupStatus={
            provisioningComponents.find(c => c.id === EVENT_TYPES.AD_GROUPS) ||
            provisioningComponents[2]
          }
          adGroupCount={adGroups.length}
          githubStatus={
            provisioningComponents.find(
              c => c.id === EVENT_TYPES.GITHUB_REPO,
            ) || provisioningComponents[1]
          }
          githubRepos={githubRepos}
          cnapStatus={
            provisioningComponents.find(
              c => c.id === EVENT_TYPES.CNAP_TENANCY,
            ) || provisioningComponents[3]
          }
          onRetry={onRetry}
        />
      )}

      {/* Support note when provisioning fails after retry */}
      {!mutationError && isFailed && hasRetried && (
        <DevHubBox mt={2}>
          <DevHubAlert severity="warning">
            <DevHubTypography variant="body2">
              If provisioning failures persist after retrying, please contact
              support for assistance.
            </DevHubTypography>
          </DevHubAlert>
        </DevHubBox>
      )}
    </DevHubBox>
  );
};
